/**
 * Sortable wrapper around BinomPathListRow. Adds a grip handle on the left
 * (drag-only) and wires @dnd-kit/sortable transforms.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { BinomDraftPath } from '../../binom-flow-draft.types';

import BinomPathListRow from './BinomPathListRow';

export interface BinomDraggablePathRowProps {
  id: string;
  path: BinomDraftPath;
  selected: boolean;
  weightPercent: number;
  onSelect: () => void;
  onPatch: (mutator: (p: BinomDraftPath) => void) => void;
  dragDisabled?: boolean;
}

const BinomDraggablePathRow: FC<BinomDraggablePathRowProps> = ({
  id,
  path,
  selected,
  weightPercent,
  onSelect,
  onPatch,
  dragDisabled = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: dragDisabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-stretch',
        isDragging && 'opacity-60 ring-1 ring-primary/40',
      )}
    >
      {!dragDisabled ? (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag ${path.name || path.id}`}
          className="flex shrink-0 cursor-grab items-center justify-center px-1 text-muted-foreground/60 hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-3 w-3" aria-hidden />
        </button>
      ) : (
        <span className="w-4 shrink-0" aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        <BinomPathListRow
          path={path}
          selected={selected}
          weightPercent={weightPercent}
          onSelect={onSelect}
          onPatch={onPatch}
        />
      </div>
    </div>
  );
};

export default BinomDraggablePathRow;
